import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, ArrowDownLeft, ArrowRight, ArrowUpRight, Banknote, Scale } from "lucide-react";
import Panel from "../../components/Panel";
import StatCard from "../../components/StatCard";
import { EmptyState, PageHeader, Pagination } from "../../components/erp";
import { financeAPI } from "../../services/businessErpService";
import { formatCents, formatDate, financeStatusClass, titleCase } from "./financeUtils";
import { Dialog } from "../../../components/ui";

function formatSignedCents(value) {
  const cents = Number(value || 0);
  return cents < 0 ? `-${formatCents(Math.abs(cents))}` : formatCents(cents);
}

const DASHBOARD_ACTION_CLASS = "inline-flex items-center gap-1.5 rounded-xl border border-neutral-300 bg-white px-3 py-2 text-xs font-semibold text-neutral-700 shadow-sm transition hover:border-neutral-400 hover:bg-neutral-50 hover:text-neutral-950 dark:border-neutral-600 dark:bg-neutral-950 dark:text-neutral-200 dark:hover:border-neutral-500 dark:hover:bg-neutral-800 dark:hover:text-white";
const ACTIVITY_PAGE_SIZE = 8;

function FinanceActivityModal({ open, items, page, onPageChange, onClose }) {
  if (!open) return null;

  const totalPages = Math.max(1, Math.ceil(items.length / ACTIVITY_PAGE_SIZE));
  const start = (page - 1) * ACTIVITY_PAGE_SIZE;
  const visibleItems = items.slice(start, start + ACTIVITY_PAGE_SIZE);

  return (
    <Dialog
      open
      onOpenChange={(next) => { if (!next) onClose(); }}
      title="All finance activity"
      description="Approvals, settlements, collections, and payroll-linked events loaded by the dashboard."
      size="wide"
      closeOnOutsideClick
    >
          <div className="overflow-hidden rounded-panel border border-default">
            {visibleItems.map((item, index) => (
              <div key={item.id} className={`flex flex-col gap-2 px-4 py-3.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4 ${index ? "border-t border-subtle" : ""}`}>
                <div className="flex min-w-0 items-start gap-3">
                  <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-control bg-surface-sunken text-secondary">
                    <Activity className="h-4 w-4" />
                  </span>
                  <div className="min-w-0">
                    <div className="text-body-sm font-medium text-foreground">{item.description || titleCase(item.action_type)}</div>
                    <div className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">{titleCase(item.category)} • {item.actor_name || "System"}</div>
                  </div>
                </div>
                <div className="shrink-0 pl-11 text-meta text-secondary sm:pl-0">{formatDate(item.created_at)}</div>
              </div>
            ))}
          </div>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-subtle pt-4">
          <span className="text-meta text-secondary">Showing {start + 1}-{Math.min(start + ACTIVITY_PAGE_SIZE, items.length)} of {items.length} loaded events</span>
          <Pagination page={page} totalPages={totalPages} onPageChange={onPageChange} />
        </div>
    </Dialog>
  );
}

export default function FinanceDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activityOpen, setActivityOpen] = useState(false);
  const [activityPage, setActivityPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await financeAPI.getDashboard();
      setData(res?.data || null);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const summary = data?.summary || {};
  const recentDisbursements = Array.isArray(data?.recent_disbursements) ? data.recent_disbursements : [];
  const recentCollections = Array.isArray(data?.recent_collections) ? data.recent_collections : [];
  const timeline = Array.isArray(data?.timeline) ? data.timeline : [];
  const payroll = Array.isArray(data?.payroll) ? data.payroll : [];
  const settledOutflow = Number(summary.settled_outflow_cents || 0);
  const collectedInflow = Number(summary.collected_inflow_cents || 0);
  const openExposure = Number(summary.open_payables_cents || 0) + Number(summary.open_receivables_cents || 0);
  const payrollTotals = payroll.reduce((totals, row) => ({
    netPay: totals.netPay + Number(row.net_pay_cents || 0),
    open: totals.open + Number(row.payroll_open_cents || 0),
    settled: totals.settled + Number(row.payroll_settled_cents || 0),
  }), { netPay: 0, open: 0, settled: 0 });

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Finance"
        title="Finance Dashboard"
        subtitle="Understand the business's financial position, money movement, exposure, and recently completed finance activity."
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard title="Collected Inflow" value={loading ? "—" : formatCents(collectedInflow)} icon={ArrowDownLeft} />
        <StatCard title="Settled Outflow" value={loading ? "—" : formatCents(settledOutflow)} icon={ArrowUpRight} />
        <StatCard title="Net Cash Movement" value={loading ? "—" : formatSignedCents(collectedInflow - settledOutflow)} icon={Scale} accent />
        <StatCard title="Open Exposure" value={loading ? "—" : formatCents(openExposure)} icon={Banknote} />
      </div>

      <Panel
        title="Finance Snapshot"
        subtitle="Current operational money position"
        right={
          <Link to="/business/finance/analytics" className={DASHBOARD_ACTION_CLASS}>
            View analytics <ArrowRight className="h-4 w-4" />
          </Link>
        }
        contentClassName="!p-0"
      >
        <div className="grid sm:grid-cols-2 xl:grid-cols-4">
          {[
            ["Overdue Payables", summary.overdue_payables_cents, "Past-due obligations"],
            ["Overdue Receivables", summary.overdue_receivables_cents, "Past-due collections"],
            ["Employee Exposure", summary.unsettled_employee_balance_cents, "Open employee balances"],
            ["Procurement Spend Open", summary.procurement_spend_cents, "Open supplier obligations"],
          ].map(([label, value, hint], index) => (
            <div key={label} className={`min-w-0 px-4 py-3.5 ${index ? "border-t border-neutral-200 dark:border-neutral-800 sm:border-l" : ""} ${index === 2 ? "sm:border-l-0 xl:border-l" : ""} ${index > 1 ? "xl:border-t-0" : "sm:border-t-0"}`}>
              <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-neutral-500 dark:text-neutral-400">{label}</div>
              <div className="mt-1 truncate text-lg font-semibold text-neutral-900 dark:text-neutral-100">{loading ? "—" : formatCents(value)}</div>
              <div className="mt-0.5 truncate text-xs text-neutral-500 dark:text-neutral-400">{hint}</div>
            </div>
          ))}
        </div>
      </Panel>

      <Panel
        title="Recent Finance Activity"
        subtitle="Latest completed approvals, settlements, and payroll-linked finance events"
        right={timeline.length ? (
          <button type="button" onClick={() => { setActivityPage(1); setActivityOpen(true); }} className={DASHBOARD_ACTION_CLASS}>
            View all activity <ArrowRight className="h-4 w-4" />
          </button>
        ) : <span className="text-xs text-neutral-500 dark:text-neutral-400">Latest activity</span>}
      >
        {loading ? (
          <div className="text-sm text-neutral-500 dark:text-neutral-400">Loading finance activity…</div>
        ) : !timeline.length ? (
          <EmptyState icon={Activity} title="No finance activity yet" description="Finance actions will appear here as operations create financial consequences." />
        ) : (
          <div className="overflow-hidden rounded-2xl border border-neutral-200 dark:border-neutral-800">
            {timeline.slice(0, 5).map((item, index) => (
              <div key={item.id} className={`flex items-center justify-between gap-4 px-4 py-3 ${index ? "border-t border-neutral-200 dark:border-neutral-800" : ""}`}>
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-neutral-900 dark:text-neutral-100">{item.description || titleCase(item.action_type)}</div>
                  <div className="mt-0.5 truncate text-xs text-neutral-500 dark:text-neutral-400">
                    {titleCase(item.category)} • {item.actor_name || "System"}
                  </div>
                </div>
                <div className="shrink-0 text-xs text-neutral-500 dark:text-neutral-400">{formatDate(item.created_at)}</div>
              </div>
            ))}
          </div>
        )}
      </Panel>

      <div className="grid gap-6 xl:grid-cols-2">
        <Panel
          title="Recent Disbursements"
          subtitle="Released money-out actions"
          right={<Link to="/business/finance/disbursements" className={DASHBOARD_ACTION_CLASS}>Open disbursements <ArrowRight className="h-4 w-4" /></Link>}
        >
          {loading ? (
            <div className="text-sm text-neutral-500 dark:text-neutral-400">Loading disbursements…</div>
          ) : !recentDisbursements.length ? (
            <div className="text-sm text-neutral-500 dark:text-neutral-400">No disbursements recorded yet.</div>
          ) : (
            <div className="space-y-3">
              {recentDisbursements.slice(0, 3).map((row) => (
                <div key={row.id} className="flex items-center justify-between gap-3 rounded-xl border border-neutral-200 p-3 dark:border-neutral-800">
                  <div>
                    <div className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{row.title}</div>
                    <div className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">{row.counterparty_name || "Counterparty"} • {formatDate(row.released_at || row.created_at)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{formatCents(row.amount_cents)}</div>
                    <span className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${financeStatusClass(row.status)}`}>{titleCase(row.status)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel
          title="Recent Collections"
          subtitle="Recorded money-in actions"
          right={<Link to="/business/finance/collections" className={DASHBOARD_ACTION_CLASS}>Open collections <ArrowRight className="h-4 w-4" /></Link>}
        >
          {loading ? (
            <div className="text-sm text-neutral-500 dark:text-neutral-400">Loading collections…</div>
          ) : !recentCollections.length ? (
            <div className="text-sm text-neutral-500 dark:text-neutral-400">No collections recorded yet.</div>
          ) : (
            <div className="space-y-3">
              {recentCollections.slice(0, 3).map((row) => (
                <div key={row.id} className="flex items-center justify-between gap-3 rounded-xl border border-neutral-200 p-3 dark:border-neutral-800">
                  <div>
                    <div className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{row.title}</div>
                    <div className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">{row.counterparty_name || "Counterparty"} • {formatDate(row.collected_at || row.created_at)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{formatCents(row.amount_cents)}</div>
                    <span className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${financeStatusClass(row.status)}`}>{titleCase(row.status)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      <Panel title="Payroll Financial Position" subtitle="Aggregated payroll value across the most recent finance-linked periods">
        {loading ? (
          <div className="text-sm text-neutral-500 dark:text-neutral-400">Loading payroll snapshot…</div>
        ) : !payroll.length ? (
          <div className="text-sm text-neutral-500 dark:text-neutral-400">No payroll periods available yet.</div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {[
              ["Periods Tracked", String(payroll.length), "Recent payroll periods included"],
              ["Total Net Pay", formatCents(payrollTotals.netPay), "Combined employee net-pay value"],
              ["Open Payroll Exposure", formatCents(payrollTotals.open), "Payroll value not yet settled"],
              ["Settled Payroll", formatCents(payrollTotals.settled), "Payroll value already settled"],
            ].map(([label, value, hint]) => (
              <div key={label} className="rounded-2xl border border-neutral-200 bg-neutral-50 px-4 py-3 dark:border-neutral-800 dark:bg-neutral-900/50">
                <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-neutral-500 dark:text-neutral-400">{label}</div>
                <div className="mt-2 text-lg font-semibold text-neutral-900 dark:text-neutral-100">{value}</div>
                <div className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">{hint}</div>
              </div>
            ))}
          </div>
        )}
      </Panel>

      <FinanceActivityModal
        open={activityOpen}
        items={timeline}
        page={activityPage}
        onPageChange={setActivityPage}
        onClose={() => setActivityOpen(false)}
      />
    </div>
  );
}
